import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useStore } from "../../../store/store";
import { SocketEvents } from "../../hooks/useSocket/types";
import useCreateQuestion from "../../hooks/useCreateQuestion";
import { IProps } from "./types";
import DialogueInput from "./Inputs/DialogueInput";
import AddDialogueButton from "./AddDialogueButton";
import useCreateMessage from "../../hooks/useCreateMessage";
import ShowWithAnimation from "../../../components/ShowWithAnimation/ShowWithAnimation";
import CustomToast from "../../../components/CustomToast";

const DialogueForm = (props: IProps) => {
	const { t } = useTranslation();
	const listenerFullName = useStore((state) => state.listenerFullName);
	const question = useStore((state) => state.question);
	const setQuestion = useStore((state) => state.setQuestion);
	const setShowFailToast = useStore((state) => state.setShowFailToast);
	const setShowSuccessToast = useStore((state) => state.setShowSuccessToast);
	const setShowForm = useStore((state) => state.setShowForm);
	const socket = useStore((state) => state.socket);
	const isCommentValidation = useStore((state) => state.isCommentValidation);
	const liveId = useStore((state) => state.liveId);
	const createQuestionMutation = useCreateQuestion(liveId);
	const createMessageMutation = useCreateMessage();
	const user = useStore((state) => state.user);
	const setFailMessage = useStore((state) => state.setFailMessage);
	const questionToAnswerId = useStore((state) => state.questionToAnswer);
	const failMessage = useStore((state) => state.failMessage);
	const showFailToast = useStore((state) => state.showFailToast);
	const messageToReply = useStore((state) => state.messageToReply);
	const setMessageToReply = useStore((state) => state.setMessageToReply);

	useEffect(() => {
		if (createQuestionMutation.isSuccess && props.type !== "message") {
			if (socket?.readyState === WebSocket.OPEN) {
				const questionData = createQuestionMutation.data?.data?.question ?? createQuestionMutation.data?.data?.data;
				socket?.send(
					JSON.stringify({
						type: SocketEvents.question,
						data: questionData,
						toAdminOnly: isCommentValidation,
					})
				);
			}
			setQuestion("");
			setShowForm(false);
			if (isCommentValidation) {
				setShowSuccessToast(true);
				setTimeout(() => {
					setShowSuccessToast(false);
				}, 3000);
			}
		}
    // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [createQuestionMutation.isSuccess, createQuestionMutation.data]);

	useEffect(() => {
		if (createMessageMutation.isSuccess && props.type === "message") {
			if (socket?.readyState === WebSocket.OPEN) {
				const messageData = createMessageMutation.data?.data?.message ?? createMessageMutation.data?.data?.data;
				socket?.send(
					JSON.stringify({
						type: SocketEvents.chat,
						data: messageData,
					})
				);
			}
			setQuestion("");
			setShowForm(false);
			if (messageToReply) {
				setMessageToReply(null);
			}
		}
    // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [createMessageMutation.isSuccess, createMessageMutation.data]);

	const sendQuestion = () => {
		if (listenerFullName.trim() === "" || question.trim() === "") {
			setShowFailToast(true);
			setFailMessage(t("signup_page.fill_all_fields"));
			setTimeout(() => {
				setShowFailToast(false);
			}, 3000);
			return;
		}
		if (props.type === "message") {
			createMessageMutation.mutate({
				liveId: liveId,
				message: question.trim(),
				fullName: listenerFullName.trim(),
				...(messageToReply && { replyMessageId: messageToReply?.id }),
				userRole: user?.role ?? "listener",
				type: "general",
			});
		} else {
			createQuestionMutation.mutate({
				liveId: liveId,
				question: question.trim(),
				fullName: listenerFullName.trim(),
				userRole: user?.role ?? "listener",
				type: props.type,
				approved: !isCommentValidation,
				...(props.type === "answer" && {
					questionId: questionToAnswerId,
				}),
			});
		}
	};
	const submitQuestion = async (e: React.FormEvent) => {
		e.preventDefault();
		sendQuestion();
	};

	return (
		<form
			onSubmit={submitQuestion}
			className="relative flex w-full flex-col items-center justify-center gap-6 "
		>
			<ShowWithAnimation isMounted={showFailToast}>
				<CustomToast
					bottom={true}
					backgroundColor="#FF333333"
					textColor="#FF3333"
					message={failMessage}
				/>
			</ShowWithAnimation>
			<p className="w-full text-sm font-medium text-secondary">
				{t("live_page.posting_as")}: <span className="text-black">{listenerFullName}</span>
			</p>
			<DialogueInput
				type={props.type}
				sendQuestion={sendQuestion}
				isLoading={
					props.type === "message"
						? createMessageMutation.isLoading
						: createQuestionMutation.isLoading
				}
			/>
			<AddDialogueButton
				isLoading={
					props.type === "message"
						? createMessageMutation.isLoading
						: createQuestionMutation.isLoading
				}
				type={props.type}
			/>
		</form>
	);
};

export default DialogueForm;
